import React, { useState, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { getCustomerDetails } from '@gjensidige/se-my-pages-utils';
import { fetchCallRequest, getUserAuthLevel } from '../api/api/call-request';
import { dispatchProperMessage } from '../api/useStatusCode';
import { preparePayload } from '../api/utils';
import {
  appText,
  customerSelectionOptions,
  getQueueDetails,
  daysWithLabelAndValue,
  defaultWorkingHours,
  holidays
} from '../constants/texts';
import { reducer } from '../state/reducer';
import { CallMeActionKind, DayType } from '../types';
import UiCallMe from './ui-call-me/UiCallMe';
import { getSortedDaysOptions, getTimeslotOptions } from '../api/utils';
import { EnonicDataType } from '../types';
import { SWEDEN_REGEX } from '../constants/texts';
import { logMessage } from '../../utils/farologger';

export type OpenHoursKey = 'open_hours_MANDAG' | 'open_hours_TISDAG' | 'open_hours_ONSDAG' | 'open_hours_TORSDAG' | 'open_hours_FREDAG';

export function MyPagesFeaturesCallMeLater({ isScheduledCall, configs }: {
  isScheduledCall?: boolean,
  configs?: EnonicDataType
}) {

  function formatPhoneNumber(number: string): string {
    let result;
    if (number.length === 10 && number.charAt(0) === '0') {
      result = "+46" + number.substring(1);
    } else {
      result = "+46" + number;
    }

    return result;
  }
  const todayDate = new Date(Date.now());

  const defaultOpenHours = configs?.open_hours || defaultWorkingHours;
  const enonicConfigs = {
    open_hours: defaultOpenHours,
    open_hours_MANDAG: configs?.open_hours_MANDAG || defaultOpenHours,
    open_hours_TISDAG: configs?.open_hours_TISDAG || defaultOpenHours,
    open_hours_ONSDAG: configs?.open_hours_ONSDAG || defaultOpenHours,
    open_hours_TORSDAG: configs?.open_hours_TORSDAG || defaultOpenHours,
    open_hours_FREDAG: configs?.open_hours_FREDAG || defaultOpenHours,
    lunch_break: configs?.lunch_break || '12-14',
    number_of_days_open: Number(configs?.number_of_days_open) || 4,
  }
  const sortedDaysOptions = getSortedDaysOptions(
    daysWithLabelAndValue,
    todayDate,
    holidays,
    enonicConfigs.number_of_days_open
  );


  const [day, setDay] = useState(sortedDaysOptions[0]);

  const chosenDay = Object.keys(daysWithLabelAndValue).find(
    (dayKey) =>
      daysWithLabelAndValue[dayKey as keyof typeof daysWithLabelAndValue]
        .value === day.value,
  );
  // To be configured via Enonic
  const timeslotRange = chosenDay ? enonicConfigs[`open_hours_${chosenDay}` as OpenHoursKey] : enonicConfigs.open_hours;
  const rangeParts = timeslotRange.split('-').map((n: string) => Number(n));
  const open = rangeParts[0];
  const close = rangeParts[1];
  const availableTimeslots = getTimeslotOptions(
    open,
    close,
    day.value,
    todayDate,
    enonicConfigs.lunch_break
  );

  const urlParams = new URLSearchParams(window.location.search);
  const name = urlParams.get('name') || '';
  const number = urlParams.get('number') || '';
  const re = new RegExp(SWEDEN_REGEX);

  const [contactFormData, dispatch] = React.useReducer(reducer, {
    callMeActionStatus: 'initial',
    name,
    number,
    day: "", // Setting Empty - good for user choice
    time: "", // Setting Empty - good for user choice
    formErrors: {
      nameInvalid: false,
      numValidity: number && !re.test(number) ? 'INVALID' : 'VALID'
    },
    isCustomer: '',
    callMeActionStatusMessage: ''
  });



  React.useEffect(() => {
    getUserAuthLevel().then((authLevel) => {
      if (authLevel && authLevel.value === 3) {
        getCustomerDetails().then((value) => {
          if (value) {
            dispatch({
              payload: value.name,
              type: CallMeActionKind.ENTER_NAME
            });
            dispatch({
              payload:
                value.phone.find((number) => number.primaryPhone)
                  ?.phoneNumber || '',
              type: CallMeActionKind.ENTER_NUM
            });
          }
        });
      }
    });
  }, []);
  const onChangeName = (name: string) => {
    dispatch({
      payload: name,
      type: CallMeActionKind.ENTER_NAME
    });
  };

  const onChangeNum = (num: string) => {
    dispatch({
      payload: num,
      type: CallMeActionKind.ENTER_NUM
    });
    debouncedMyFunction(num);
  };

  const onChangeDay = (day: DayType) => {
    setDay(day);
    dispatch({
      payload: day,
      type: CallMeActionKind.CHOOSE_DAY
    });
  }

  const onChangeTime = (time: { label: string, value: Date } | string) => {
    dispatch({
      payload: time,
      type: CallMeActionKind.CHOOSE_TIME
    });
  }

  const debouncedMyFunction = React.useMemo(
    () =>
      debounce((num: string) => {
        dispatch({
          payload: num,
          type: CallMeActionKind.VALIDATE_ENTER_NUM
        });
      }, 750),
    [dispatch]
  );


  const onSelectCustomerRelationShip = (option: string) => {
    dispatch({
      type: CallMeActionKind.SELECT_CUSTOMER_RELATIONSHIP,
      payload: option
    });
  };
  const onFormSubmit = React.useCallback(() => {
    dispatch({
      payload: '',
      type: CallMeActionKind.SET_LOADING
    });
    const queDetails = getQueueDetails(configs, !!isScheduledCall);
    const callMePayload = preparePayload(contactFormData, queDetails, !!isScheduledCall);
    fetchCallRequest(callMePayload).then((value) => {
      dispatchProperMessage(value.code, dispatch);
    });
    logMessage('Call me later request published', { severity: 'INFO' });
  }, [contactFormData]);

  return (
    <UiCallMe
      appText={appText}
      contactFormData={contactFormData}
      events={{
        onFormSubmit,
        onChangeName,
        onChangeNum,
        onChangeDay,
        onChangeTime,
        onSelectCustomerRelationShip
      }}
      customerSelectionOptions={customerSelectionOptions}
      isScheduledCall={!!isScheduledCall}
      sortedDaysOptions={sortedDaysOptions}
      availableTimeslots={availableTimeslots}
    />
  );
}

export default MyPagesFeaturesCallMeLater;


